/**
 * RPA Flows Service — Phase 3b
 *
 * Thin client over governance-service /api/v1/rpa/* (routed via the api-gateway
 * `/api/` fallback to governance-service). Used by the Flow management page and
 * the Copilot "save session as flow" dialog.
 */

import { get, post, put, del } from '@/lib/api/client'

// ── Types ─────────────────────────────────────────────────────────────────────

export type FlowTrigger = 'manual' | 'schedule' | 'event'

export interface RpaFlow {
  id: string
  tenant_id?: string
  name: string
  description?: string
  owner_user_id?: string | null
  trigger: FlowTrigger
  schedule_cron?: string | null
  event_topic?: string | null
  params_schema?: Record<string, unknown>
  steps?: Array<Record<string, unknown>>
  requires_confirmation?: boolean
  dry_run_default?: boolean
  version?: number
  status?: string
  created_at?: string
  // List-only enrichments
  run_count?: number
  last_run_status?: string | null
}

export interface CreateFlowInput {
  name: string
  description?: string
  trigger?: FlowTrigger
  schedule_cron?: string
  event_topic?: string
  params_schema?: Record<string, unknown>
  steps?: Array<Record<string, unknown>>
  requires_confirmation?: boolean
  dry_run_default?: boolean
}

export interface RpaRun {
  id?: string
  run_id?: string
  flow_id: string
  status: string
  dry_run?: boolean
  trigger_kind?: string
  step_results?: Array<Record<string, unknown>>
  steps_validated?: number
  created_at?: string
  message?: string
}

interface ListResponse<T> {
  data: T[]
  count: number
}

const BASE = '/rpa'

// ── Flows ─────────────────────────────────────────────────────────────────────

export async function listFlows(): Promise<RpaFlow[]> {
  const res = await get<ListResponse<RpaFlow>>(`${BASE}/flows`)
  return res?.data ?? []
}

export async function getFlow(id: string): Promise<RpaFlow> {
  return get<RpaFlow>(`${BASE}/flows/${id}`)
}

export async function createFlow(input: CreateFlowInput): Promise<RpaFlow> {
  return post<RpaFlow, CreateFlowInput>(`${BASE}/flows`, {
    trigger: 'manual',
    requires_confirmation: true,
    dry_run_default: false,
    params_schema: {},
    steps: [],
    ...input,
  })
}

export async function updateFlow(id: string, input: Partial<CreateFlowInput> & { status?: string }): Promise<RpaFlow> {
  return put<RpaFlow>(`${BASE}/flows/${id}`, input)
}

export async function deleteFlow(id: string): Promise<{ id: string; deleted: boolean }> {
  return del<{ id: string; deleted: boolean }>(`${BASE}/flows/${id}`)
}

export async function runFlow(
  id: string,
  body: { params?: Record<string, unknown>; dry_run?: boolean; trigger_kind?: string } = {}
): Promise<RpaRun> {
  return post<RpaRun>(`${BASE}/flows/${id}/run`, body)
}

// ── Runs ──────────────────────────────────────────────────────────────────────

export async function listRuns(flowId?: string): Promise<RpaRun[]> {
  const qs = flowId ? `?flow_id=${encodeURIComponent(flowId)}` : ''
  const res = await get<ListResponse<RpaRun>>(`${BASE}/runs${qs}`)
  return res?.data ?? []
}

export async function getRun(id: string): Promise<RpaRun> {
  return get<RpaRun>(`${BASE}/runs/${id}`)
}
